import React, { useEffect, useState } from "react";
import { Alert, Button, DatePicker, Form, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Success } from "../../../commons/Messages/Messages";
import { checkPlan, updateJob } from "./api";
import { clockToMinutes, dayOf, shiftRange } from "./calc";
import { ConflictList, saveWithAcknowledgement } from "./Conflicts";
import { clockOf } from "./labels";

const CLOCKS = Array.from(
  { length: 96 },
  (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`
);

/**
 * "Move to…" on phones: another day, start time and/or person for a planned item, keeping its
 * duration. The conflicts of the new slot are shown before saving.
 */
const MoveDialog = ({ open, item, from, staff, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setConflicts([]);
    form.setFieldsValue({
      day: dayjs(dayOf(item.start)),
      startTime: clockOf(item.start),
      employees: item.employees
    });
  }, [open, item]);

  const target = () => {
    const v = form.getFieldsValue();
    if (!v.day || !v.startTime) return null;
    const range = shiftRange(item, {
      day: v.day.format("YYYY-MM-DD"),
      startMinutes: clockToMinutes(v.startTime)
    });
    return {
      start: range.start.toISOString(),
      end: range.end.toISOString(),
      employees: v.employees || []
    };
  };

  const preview = async () => {
    const body = target();
    if (!body) return;
    try {
      const res = await checkPlan({
        ...body,
        appointment: item.id,
        vehicle: item.vehicle,
        tools: item.tools,
        driver: item.driver,
        requiredSkills: item.requiredSkills,
        requiredQualifications: item.requiredQualifications
      });
      setConflicts(res.data.data);
    } catch {
      setConflicts([]);
    }
  };

  useEffect(() => {
    if (open && item) preview();
  }, [open, item]);

  const save = async () => {
    const body = target();
    if (!body) return;
    setSaving(true);
    const res = await saveWithAcknowledgement(acknowledge =>
      updateJob(item.id, { ...body, acknowledge })
    );
    setSaving(false);
    if (res) {
      Success(t("Saved"));
      onSaved();
    }
  };

  return (
    <Modal
      rootClassName="dispatch-page"
      open={open}
      onCancel={onClose}
      title={item ? `${t("Move to…")} ${item.title}` : ""}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={save}>
          {t("Move")}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical" onValuesChange={preview}>
        <Form.Item name="day" label={t("Day")}>
          <DatePicker className="dsp-full" format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        <Form.Item name="startTime" label={t("Start")}>
          <Select options={CLOCKS.map(c => ({ value: c, label: c }))} />
        </Form.Item>
        <Form.Item
          name="employees"
          label={t("Employees")}
          extra={from ? t("Replace the person to hand the job over.") : null}
        >
          <Select
            mode="multiple"
            optionFilterProp="label"
            options={(staff || []).map(s => ({ value: s.id, label: s.name }))}
          />
        </Form.Item>
      </Form>
      {conflicts.length > 0 && (
        <Alert
          type={conflicts.some(c => c.severity === "warning") ? "warning" : "info"}
          showIcon
          description={<ConflictList conflicts={conflicts} t={t} />}
        />
      )}
    </Modal>
  );
};

export default MoveDialog;
